import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Resize via Supabase's render endpoint — way faster than loading the
// original full-resolution upload (often 3-5 MB straight from a phone
// camera). Pass-through for non-Supabase URLs.
function fastImage(url, width = 1600) {
  if (!url || !url.includes('/storage/v1/object/public/')) return url
  return url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/') + `?width=${width}&resize=contain&quality=80`
}

export default function ImageLightbox({ src, images, initialIndex = 0, onClose }) {
  // Back-compat: callers can pass a single `src` (single image) or
  // `images` + `initialIndex` (carousel). Normalize to one array.
  const list = images?.length ? images : (src ? [src] : [])
  const [index, setIndex] = useState(initialIndex)
  const scrollerRef = useRef(null)
  // Guards handleScroll from echoing intermediate scroll positions back to
  // index during a programmatic smooth-scroll (fights the arrow-click / key
  // handlers otherwise).
  const isProgrammaticScroll = useRef(false)

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, list.length - 1))
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0))
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose, list.length])

  // When index changes via keyboard or arrow button, sync the scroll position.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    isProgrammaticScroll.current = true
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' })
    // Smooth-scroll animation typically lands within ~400ms.
    const t = setTimeout(() => { isProgrammaticScroll.current = false }, 500)
    return () => clearTimeout(t)
  }, [index])

  // When user swipes/scrolls manually, derive the active index from scroll
  // position. Ignore scroll events during a programmatic scrollTo so the
  // in-flight smooth-scroll doesn't fight the arrow-click state update.
  function handleScroll() {
    if (isProgrammaticScroll.current) return
    const el = scrollerRef.current
    if (!el) return
    const i = Math.round(el.scrollLeft / el.clientWidth)
    if (i !== index) setIndex(i)
  }

  if (!list.length) return null

  // Portalled to <body>. Rendered in place, the overlay inherits whatever
  // stacking context its host sits in — the league thread mounts inside a
  // `relative z-10` wrapper, so this z-50 was scoped to z-10 and the navbar
  // (sticky top-0 z-50 at page root) painted straight over the top strip.
  // The image filled the screen and the close button sat behind the navbar;
  // only a swipe got you out. Same rule as every other modal here.
  return createPortal(
    (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose() }}
        aria-label="Close"
        // z-50 so nothing (image, scroller, arrows) can obscure the close
        // affordance. Border ring bumps contrast on light images.
        className="absolute right-3 text-white text-3xl z-50 w-12 h-12 flex items-center justify-center rounded-full bg-black/60 ring-1 ring-white/30 active:bg-black/80 hover:bg-black/80"
        style={{ top: 'max(0.75rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
      >
        ×
      </button>

      {list.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-xs z-20 bg-black/40 px-2 py-1 rounded-full">
          {index + 1} / {list.length}
        </div>
      )}

      {/* Horizontal scroll-snap carousel — native swipe on touch devices,
          no extra gesture library needed. Each slide is exactly the
          viewport width so snap points land cleanly. */}
      {/* Scroller has NO onClick stopPropagation — that used to intercept
          every click in the viewport (scroller is w-full h-full) so the
          outer backdrop click-to-close never fired. Instead, stop
          propagation only on the actual image element so click-on-image
          doesn't close but click-on-empty-space does. */}
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="w-full h-full flex overflow-x-auto snap-x snap-mandatory overscroll-contain scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {list.map((url, i) => (
          <div
            key={url + i}
            // pt-16 leaves headroom for the close button (48px tall + top offset).
            // Without it, tall portrait images get clipped behind the X and users
            // can't see the button OR the top of the image.
            className="shrink-0 w-full h-full flex items-center justify-center snap-center px-4 pt-16 pb-6"
          >
            <img
              src={fastImage(url)}
              alt=""
              loading={Math.abs(i - initialIndex) <= 1 ? 'eager' : 'lazy'}
              onClick={(e) => e.stopPropagation()}
              // Cap at the interior height (viewport minus the padding above/below)
              // so tall portraits fit entirely without overflowing behind chrome.
              className="max-w-full max-h-[calc(100vh-6rem)] object-contain rounded-lg"
            />
          </div>
        ))}
      </div>

      {list.length > 1 && (
        <>
          {index > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setIndex((i) => Math.max(i - 1, 0)) }}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-black/40 text-white/80 hover:text-white z-20"
              aria-label="Previous image"
            >
              ‹
            </button>
          )}
          {index < list.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setIndex((i) => Math.min(i + 1, list.length - 1)) }}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-black/40 text-white/80 hover:text-white z-20"
              aria-label="Next image"
            >
              ›
            </button>
          )}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
            {list.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/40'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
    ),
    document.body,
  )
}
